/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * NLS (Natural Language Search) GraphQL Service
 * Handles conversational search queries using the NLS UXL API
 *
 * All search parameters (dates, occupancy, rates, fees) are passed via the
 * structured `search.options` object. The `search.query` field carries only
 * the raw natural language intent — sourced from the SSE event's top-level
 * `payload.query` field (sibling to `rawData`), with `llmMetadata.llmInfo.queryRewrite`
 * as a fallback.
 */

import {
  phoenixConversationalSearchByNaturalLanguage,
  phoenixConversationalSearchByNaturalLanguageFilter,
} from '../queries';
import { useMarriottAIPreviewStore } from '../store';
import {
  NLSSearchParams,
  NLSSearchVariables,
  NLSSearchOptions,
  NLSRateRequestType,
  NLSGraphQLResponse,
  NLSSearchByNaturalLanguageQueryResult,
  NLSFacetsPayload,
  NLSSelectedFacetMap,
  NLS_PRICE_ENDPOINTS,
  NLS_DISTANCE_ENDPOINTS,
  NLS_POINTS_ENDPOINTS,
  NLPEntitiesMap,
} from './nlsSearch.types';

/**
 * Build GraphQL variables for NLS search.
 *
 * Follows the exact payload pattern of SearchResultsComponent in mi-shop-components
 * (see helper.tsx → defaultObj.options and submitSearchRequestHelper.ts → searchObj.options):
 *
 *   search.query    — natural language intent (payload.query from SSE event, or llmMetadata.queryRewrite as fallback)
 *   search.options:
 *     startDate               → check-in date in YYYY-MM-DD
 *     endDate                 → check-out date in YYYY-MM-DD
 *     quantity                → number of rooms (minimum 1)
 *     numberInParty           → adults + children per room
 *     childAges               → flat array of child ages, omitted when no children
 *     rateRequestTypes        → mirrors constructRateTypes / clusterCodeToRateRequestTypes
 *     includeMandatoryFees    → true (chat is EN-US only)
 *     includeTaxesAndFees     → false (shop default)
 *     includeUnavailableProperties → true
 *     customerId              → consumerId from MarriottAIPreviewStore (null when unauthenticated)
 *   search.facets:
 *     terms[]                 → term facets (AMENITIES, BRANDS, PROPERTY_TYPES, …)
 *     ranges[]                → range facets (PRICE, DISTANCE) with endpoints
 */

/**
 * Build the `search.facets` payload from a selected facet map.
 *
 * Mirrors shop's getSearchAppliedFilter() in store.action.tsx.
 * Term facets: AMENITIES, BRANDS, PROPERTY_TYPES, HOTEL_SERVICE_TYPES, ACTIVITIES, …
 * Range facets: PRICE (endpoints 0/100/200/overflow), DISTANCE (endpoints in metres)
 *
 * @param selectedFacets - Map of { [FACET_TYPE]: { [dimensionCode]: dimensionCode } }
 * @returns NLSFacetsPayload — { terms, ranges } to set on search.facets
 */

/**
 * Description types passed as $filter to the main NLS query.
 * Mirrors shop's descriptionFilterToDisplayOnPropertyCard constants.
 */
const NLS_DESCRIPTION_FILTER = [
  'HOTEL_MARKETING_CAPTION',
  'RESORT_FEE_DESCRIPTION',
  'DESTINATION_FEE_DESCRIPTION',
  'TOURISM_MARKETING_FEE_DESCRIPTION',
  'SURCHARGE_ORDINANCE_COST_DESCRIPTION',
];

/**
 * All term facet types — matches shop's SearchFacetsList term values
 * (every key that is NOT price/distance/points in the enum).
 * Order mirrors shop's getInitialSearchFacets / getSearchAppliedFilter.
 */
const NLS_TERM_TYPES = [
  'BRANDS',
  'AMENITIES',
  'PROPERTY_TYPES',
  'TRANSPORTATION_TYPES',
  'HOTEL_SERVICE_TYPES',
  'CITIES',
  'COUNTRIES',
  'MEETINGS_EVENTS',
  'ACTIVITIES',
  'STATES',
  'LEISURE_REGIONS',
];

/**
 * Maps nlpEntities object keys (lowercase-hyphenated, matching SearchFacetsListCodes)
 * to SearchFacetsList uppercase enum values (used as keys in searchQueryState).
 *
 * Derived from SearchFacetsList and SearchFacetsListCodes in mi-shop-components:
 *   SearchFacetsListCodes.amenities       = 'amenities'         → SearchFacetsList.amenities       = 'AMENITIES'
 *   SearchFacetsListCodes.brand           = 'brands'            → SearchFacetsList.brand           = 'BRANDS'
 *   SearchFacetsListCodes.propertyType    = 'property-types'    → SearchFacetsList.propertyType    = 'PROPERTY_TYPES'
 *   SearchFacetsListCodes.hotelType       = 'hotel-service-types'→ SearchFacetsList.hotelType      = 'HOTEL_SERVICE_TYPES'
 *   SearchFacetsListCodes.transportTypes  = 'transportation-types'→SearchFacetsList.transportTypes = 'TRANSPORTATION_TYPES'
 *   SearchFacetsListCodes.activities      = 'activities-on-site' → SearchFacetsList.activities     = 'ACTIVITIES'
 *   SearchFacetsListCodes.meetingEvents   = 'meetings-and-events'→ SearchFacetsList.meetingEvents  = 'MEETINGS_EVENTS'
 *   SearchFacetsListCodes.cities          = 'cities'             → SearchFacetsList.cities         = 'CITIES'
 *   SearchFacetsListCodes.states          = 'states'             → SearchFacetsList.states         = 'STATES'
 *   SearchFacetsListCodes.countries       = 'countries'          → SearchFacetsList.countries      = 'COUNTRIES'
 *   SearchFacetsListCodes.leisureRegions  = 'leisure-region'     → SearchFacetsList.leisureRegions = 'LEISURE_REGIONS'
 *
 * Note: TIP may emit 'hotel-types' (not 'hotel-service-types') — both are mapped.
 */
const NLP_ENTITY_KEY_TO_FACET_TYPE: Record<string, string> = {
  amenities: 'AMENITIES',
  brands: 'BRANDS',
  'property-types': 'PROPERTY_TYPES',
  'hotel-service-types': 'HOTEL_SERVICE_TYPES',
  'hotel-types': 'HOTEL_SERVICE_TYPES', // TIP sometimes uses this shorter key
  'transportation-types': 'TRANSPORTATION_TYPES',
  activities: 'ACTIVITIES',
  'activities-on-site': 'ACTIVITIES',
  'meetings-and-events': 'MEETINGS_EVENTS',
  cities: 'CITIES',
  states: 'STATES',
  countries: 'COUNTRIES',
  'leisure-region': 'LEISURE_REGIONS',
};

/**
 * Convert the `nlpEntities` object returned by the TIP/NLS response into an
 * NLSSelectedFacetMap that pre-selects filters in the All Filters modal.
 *
 * nlpEntities shape (actual TIP response):
 *   {
 *     "amenities":      [{ label: "Pool",   code: "pool",   action: "filter" }],
 *     "property-types": [{ label: "Resort", code: "resort", action: "boost"  }],
 *     "countries":      [{ label: "Italy",  code: "IT",     action: "boost"  }]
 *   }
 *
 * Rules:
 *  - nlpEntities is an object keyed by lowercase-hyphenated facet category.
 *  - Each value is an array of entities with { label, code?, action }.
 *  - Only entities whose `code` is present AND whose `action` is 'filter'
 *    are surfaced as pre-selected (boost-only or code-less entries are skipped).
 *  - Keys are mapped to uppercase SearchFacetsList values via NLP_ENTITY_KEY_TO_FACET_TYPE.
 *
 * Output shape:  { AMENITIES: { pool: 'pool' }, PROPERTY_TYPES: { resort: 'resort' } }
 */
export function buildSelectedFacetsFromNlpEntities(
  nlpEntities: NLPEntitiesMap | null | undefined
): NLSSelectedFacetMap {
  if (!nlpEntities || typeof nlpEntities !== 'object' || Array.isArray(nlpEntities)) return {};

  const result: NLSSelectedFacetMap = {};

  for (const [rawKey, entities] of Object.entries(nlpEntities)) {
    const facetType = NLP_ENTITY_KEY_TO_FACET_TYPE[rawKey];
    if (!facetType) continue; // unknown category (e.g. 'poi', 'enrichedSearchableData')
    if (!Array.isArray(entities)) continue;

    for (const entity of entities) {
      if (!entity?.code) continue; // skip entities without a bucket code
      const action = (entity.action ?? '').toLowerCase();
      if (action !== 'filter') continue;

      if (!result[facetType]) result[facetType] = {};
      result[facetType][entity.code] = entity.code;
    }
  }

  return result;
}

export function buildNLSFacetsPayload(selectedFacets: NLSSelectedFacetMap, usePoints?: boolean): NLSFacetsPayload {
  // Always send every term type — empty dimensions[] for unselected ones.
  // Mirrors shop's getSearchAppliedFilter() which iterates all SearchFacetsList
  // values and always includes every type (selected or not).
  const terms: NLSFacetsPayload['terms'] = NLS_TERM_TYPES.map(type => ({
    type,
    dimensions: Object.keys(selectedFacets[type] ?? {}),
  }));

  // Shop sends EITHER PRICE or POINTS range (never both) based on usePoints flag.
  // Sending both causes ERR-RATES-1006: "Points And Price Range combo is invalid"
  // See mi-shop-components/src/organisms/SearchResults/store/store.action.tsx:
  //   - getInitialSearchFacets() lines 295-306: points ? POINTS : PRICE
  //   - getSearchAppliedFilter() lines 163-180: conditional based on currentState[SearchFacetsConstants.points]
  const ranges: NLSFacetsPayload['ranges'] = [
    usePoints
      ? {
          type: 'POINTS',
          dimensions: Object.keys(selectedFacets['POINTS'] ?? {}),
          endpoints: NLS_POINTS_ENDPOINTS,
        }
      : {
          type: 'PRICE',
          dimensions: Object.keys(selectedFacets['PRICE'] ?? {}),
          endpoints: NLS_PRICE_ENDPOINTS,
        },
    {
      type: 'DISTANCE',
      dimensions: Object.keys(selectedFacets['DISTANCE'] ?? {}),
      endpoints: NLS_DISTANCE_ENDPOINTS,
    },
  ];

  return { terms, ranges };
}
export function buildNLSSearchVariables(params: NLSSearchParams): NLSSearchVariables {
  const rateRequestTypes = buildRateRequestTypes(params);

  const roomsCount = params.roomsCount ?? 1;
  const adultsPerRoom = params.adultsPerRoom ?? 1;
  const childrenPerRoom = params.childrenPerRoom ?? 0;
  const childrenAges = params.childrenAges ?? [];

  const quantity = Math.max(roomsCount, 1);
  const numberInParty = Math.max(adultsPerRoom + childrenPerRoom, 1);

  // Coerce each age to an integer — GraphQL requires Int, not String.
  // Infant / < 1 year is represented as null or undefined in some store
  // implementations; treat those as 0.
  const childAgesOption: number[] | null =
    childrenPerRoom > 0 && childrenAges.length > 0
      ? (() => {
          const ages = childrenAges.slice(0, childrenPerRoom * roomsCount).map(age => {
            if (age === null || age === undefined) return 0; // infant / < 1 year
            const parsed = parseInt(String(age), 10);
            return isNaN(parsed) ? 0 : parsed; // fallback to 0 for any unparseable value
          });
          return ages.length > 0 ? ages : null;
        })()
      : null;

  const customerId = useMarriottAIPreviewStore.getState().consumerId ?? null;

  const options: NLSSearchOptions = {
    startDate: params.checkInDate ?? null,
    endDate: params.checkOutDate ?? null,
    quantity,
    numberInParty,
    childAges: childAgesOption,
    rateRequestTypes,
    includeMandatoryFees: true,
    includeTaxesAndFees: false,
    includeUnavailableProperties: params.includeUnavailableProperties ?? true,
    customerId,
  };

  // Always build the full facets payload — mirrors shop's getSearchAppliedFilter()
  // which always sends every term/range type, with empty dimensions[] for unselected ones.
  // Pass an empty map when no facets are selected so all dimensions stay [].
  // Pass usePoints to conditionally include PRICE or POINTS range (never both).
  const facets = buildNLSFacetsPayload(params.selectedFacets ?? {}, params.usePoints);

  return {
    search: {
      query: params.baseQuery,
      options,
      facets,
    },
    limit: params.limit ?? 40,
    offset: params.offset ?? 0,
    filter: NLS_DESCRIPTION_FILTER,
  };
}

/**
 * Build the `rateRequestTypes` array for `search.options`.
 *
 * Mirrors `constructRateTypes` / `clusterCodeToRateRequestTypes` in mi-shop-components
 * so the NLS API receives the same rate context as SearchResultsComponent.
 *
 * Points/rewards → three entries: MRW + STANDARD + P17
 * Standard        → single STANDARD entry with empty value
 */
function buildRateRequestTypes(params: NLSSearchParams): NLSRateRequestType[] | null {
  if (params.usePoints) {
    return [
      { type: 'CLUSTER', value: 'MRW' },
      { type: 'STANDARD', value: '' },
      { type: 'CLUSTER', value: 'P17' },
    ];
  }

  const { clusterCode, specialRateCode, corporateCode, groupCode } = params.specialRates ?? {};

  if (corporateCode) {
    return [{ type: 'CLUSTER', value: corporateCode }];
  }

  if (groupCode) {
    return [{ type: 'GROUP', value: groupCode }];
  }

  // Use specialRateCode (canonical key) when available; fall back to clusterCode
  const rateCode = (specialRateCode || clusterCode || '').toLowerCase();

  if (rateCode === 'aaa') return [{ type: 'AAA', value: 'aaa' }];
  if (rateCode === 'govt' || rateCode === 'gov') return [{ type: 'GOV', value: 'gov' }];
  if (rateCode === 'senior' || rateCode === 's9r') return [{ type: 'CLUSTER', value: 'S9R' }];

  // Non-empty cluster codes that don't match a named type
  if (clusterCode && clusterCode !== 'none' && clusterCode !== '' && clusterCode !== 'Lowest Regular Rate') {
    return [{ type: 'CLUSTER', value: clusterCode }];
  }

  // Default — Lowest Regular Rate (Standard)
  return [{ type: 'STANDARD', value: '' }];
}

/**
 * Execute NLS search query
 *
 * Note: Authorization is handled automatically by the Apollo client's getAuthLink.
 * Do NOT manually add Authorization headers here - it will cause duplicate/invalid headers.
 */
export async function executeNLSSearch(
  client: any,
  params: NLSSearchParams,
  headers?: Record<string, string>
): Promise<NLSSearchByNaturalLanguageQueryResult | null> {
  try {
    const correlationId = `nls-search-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const variables = buildNLSSearchVariables(params);

    const response: NLSGraphQLResponse = await client.query({
      query: phoenixConversationalSearchByNaturalLanguage,
      variables,
      context: {
        headers: {
          'x-correlation-id': correlationId,
          ...headers,
        },
      },
      fetchPolicy: 'network-only',
      // Return partial data alongside errors rather than throwing.
      // This allows the search results to come through even when a
      // non-critical downstream service (e.g. image CDN) returns a 503.
      errorPolicy: 'all',
    });

    if (response.errors && response.errors.length > 0) {
      const result = response.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery;
      if (!result) {
        // Fatal — no data at all alongside the errors
        console.error('[NLS Search] GraphQL errors (no data):', response.errors);
        return null;
      }
      // Partial response: log non-critical errors (e.g. image CDN 503) but
      // still return the valid search results that are present in data.
      console.warn('[NLS Search] GraphQL partial errors (data still returned):', response.errors);
      return result;
    }

    const result = response.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery;
    return result || null;
  } catch (error) {
    console.error('[NLS Search] Error executing search:', error);
    return null;
  }
}

/**
 * Execute NLS search with filter updates.
 * This is the main function called when the user updates filters in the MiniSearchBar.
 */
export async function executeNLSSearchWithFilters(
  client: any,
  baseQueryRewrite: string,
  searchFormState: {
    dates?: {
      fromDate?: any;
      toDate?: any;
      flexible?: string | boolean;
      numberOfNights?: number;
    };
    roomsAndGuests?: {
      numRooms?: string | number;
      numAdultsPerRoom?: number;
      numChildrenPerRoom?: number;
      childrenAges?: number[];
    };
    usePoints?: {
      rewardsRedemption?: boolean;
    };
    brands?: {
      marriottBrands?: string[];
    };
    specialRates?: {
      clusterCode?: string;
      specialRateCode?: string;
      corporateCode?: string;
      groupCode?: string;
    };
  },
  appliedFilters?: {
    amenities?: string[];
    priceRange?: { min?: number; max?: number; currency?: string };
    /** Selected facet dimension map — passed directly to buildNLSFacetsPayload */
    selectedFacets?: NLSSelectedFacetMap;
    /** Include unavailable properties in search results */
    includeUnavailableProperties?: boolean;
  },
  headers?: Record<string, string>,
  pagination?: { limit?: number; offset?: number }
): Promise<NLSSearchByNaturalLanguageQueryResult | null> {
  let checkInDate: string | undefined;
  let checkOutDate: string | undefined;

  if (searchFormState.dates?.fromDate) {
    const fromDate = searchFormState.dates.fromDate;
    checkInDate = typeof fromDate === 'string' ? fromDate : fromDate.format?.('YYYY-MM-DD');
  }

  if (searchFormState.dates?.toDate) {
    const toDate = searchFormState.dates.toDate;
    checkOutDate = typeof toDate === 'string' ? toDate : toDate.format?.('YYYY-MM-DD');
  }

  const params: NLSSearchParams = {
    baseQuery: baseQueryRewrite,
    checkInDate,
    checkOutDate,
    roomsCount: searchFormState.roomsAndGuests?.numRooms
      ? parseInt(String(searchFormState.roomsAndGuests.numRooms), 10)
      : 1,
    adultsPerRoom: searchFormState.roomsAndGuests?.numAdultsPerRoom || 1,
    childrenPerRoom: searchFormState.roomsAndGuests?.numChildrenPerRoom || 0,
    childrenAges: searchFormState.roomsAndGuests?.childrenAges || [],
    usePoints: searchFormState.usePoints?.rewardsRedemption || false,
    brands: searchFormState.brands?.marriottBrands || [],
    specialRates: searchFormState.specialRates
      ? {
          clusterCode: searchFormState.specialRates.clusterCode,
          specialRateCode: searchFormState.specialRates.specialRateCode,
          corporateCode: searchFormState.specialRates.corporateCode,
          groupCode: searchFormState.specialRates.groupCode,
        }
      : undefined,
    amenities: appliedFilters?.amenities,
    priceRange: appliedFilters?.priceRange,
    selectedFacets: appliedFilters?.selectedFacets,
    includeUnavailableProperties: appliedFilters?.includeUnavailableProperties ?? true,
    limit: pagination?.limit || 20,
    offset: pagination?.offset || 0,
  };

  return executeNLSSearch(client, params, headers);
}

/**
 * Execute a facets-only NLS call using the Filter query (no edges).
 *
 * Fires `phoenixConversationalSearchByNaturalLanguageFilter` — mirrors shop's
 * Filter query pattern (e.g. phoenixShopSearchPropertiesByDestinationFilter).
 * Called on every individual facet click so the filter UI shows live bucket counts.
 * Does NOT update search results — only updates the facets in the filter panel.
 */
export async function executeNLSFilterRefresh(
  client: any,
  baseQueryRewrite: string,
  searchFormState: Parameters<typeof executeNLSSearchWithFilters>[2],
  selectedFacets: NLSSelectedFacetMap,
  headers?: Record<string, string>
): Promise<NLSSearchByNaturalLanguageQueryResult | null> {
  try {
    const correlationId = `nls-filter-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Build variables using same helper — includes facets payload
    let checkInDate: string | undefined;
    let checkOutDate: string | undefined;
    if (searchFormState.dates?.fromDate) {
      const fromDate = searchFormState.dates.fromDate;
      checkInDate = typeof fromDate === 'string' ? fromDate : fromDate.format?.('YYYY-MM-DD');
    }
    if (searchFormState.dates?.toDate) {
      const toDate = searchFormState.dates.toDate;
      checkOutDate = typeof toDate === 'string' ? toDate : toDate.format?.('YYYY-MM-DD');
    }

    const params: NLSSearchParams = {
      baseQuery: baseQueryRewrite,
      checkInDate,
      checkOutDate,
      roomsCount: searchFormState.roomsAndGuests?.numRooms
        ? parseInt(String(searchFormState.roomsAndGuests.numRooms), 10)
        : 1,
      adultsPerRoom: searchFormState.roomsAndGuests?.numAdultsPerRoom || 1,
      childrenPerRoom: searchFormState.roomsAndGuests?.numChildrenPerRoom || 0,
      childrenAges: searchFormState.roomsAndGuests?.childrenAges || [],
      usePoints: searchFormState.usePoints?.rewardsRedemption || false,
      specialRates: searchFormState.specialRates,
      selectedFacets,
      limit: 40,
      offset: 0,
    };

    const variables = buildNLSSearchVariables(params);

    const response: NLSGraphQLResponse = await client.query({
      query: phoenixConversationalSearchByNaturalLanguageFilter,
      variables,
      context: {
        headers: {
          'x-correlation-id': correlationId,
          ...headers,
        },
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });

    const result = response.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery;
    return result || null;
  } catch (error) {
    console.error('[NLS Filter Refresh] Error:', error);
    return null;
  }
}
